import { toDocumentImportFailureMessage } from './document-import-size-failure';
import type { JobProgressMessage } from './models';

export function toRealtimeJobLabel(
    jobType: JobProgressMessage['jobType']
): string {
    switch (jobType) {
        case 'DocumentImport':
            return 'Document import';
        case 'SharePointImport':
            return 'SharePoint import';
        case 'DocumentClassification':
            return 'Document classification';
        case 'BuildingDocumentExport':
            return 'Building document export';
        case 'DocumentPackExport':
            return 'Document pack export';
        default:
            return 'Background job';
    }
}

export function toRealtimeStatusLabel(
    status: JobProgressMessage['status']
): string {
    switch (status) {
        case 'Queued':
            return 'queued';
        case 'Started':
            return 'started';
        case 'Progress':
            return 'in progress';
        case 'Completed':
            return 'completed';
        case 'Failed':
            return 'failed';
        case 'Skipped':
            return 'skipped';
        case 'Expired':
            return 'expired';
        default:
            return assertNever(status, 'status');
    }
}

export function toRealtimeToastDuration(
    status: JobProgressMessage['status']
): number {
    switch (status) {
        case 'Failed':
            return 7_000;
        case 'Completed':
        case 'Skipped':
            return 5_500;
        default:
            return 4_500;
    }
}

export function toRealtimeToastDescription(
    message: JobProgressMessage
): string {
    if (message.jobType === 'DocumentImport') {
        const failureMessage = toDocumentImportFailureMessage({
            code: message.failureCode,
            actualBytes: message.actualBytes,
            allowedBytes: message.allowedBytes,
        });

        if (failureMessage) {
            return message.status === 'Completed'
                ? `${failureMessage} Other files were imported. Review processing history for details.`
                : `${failureMessage} Review processing history for details.`;
        }
    }

    const progressDescription =
        message.percentComplete !== null
            ? `${message.percentComplete}% complete.`
            : null;

    if (progressDescription && message.message) {
        return `${progressDescription} ${message.message}`;
    }

    if (message.message) {
        return message.message;
    }

    if (progressDescription) {
        return progressDescription;
    }

    switch (message.status) {
        case 'Completed':
            return 'The document job finished successfully.';
        case 'Failed':
            return 'The document job did not finish successfully.';
        case 'Skipped':
            return 'The document job was skipped.';
        case 'Expired':
            return 'The document job expired.';
        default:
            return 'Waiting for the next document-processing update.';
    }
}

function assertNever(value: never, label: string): never {
    throw new TypeError(`Unhandled realtime ${label}: ${String(value)}`);
}
